import React, { useState, useEffect, useRef, createRef } from 'react'
import _ from 'lodash'
import styled from 'styled-components'
import { Box, Input, Icon } from '@workwhile/ui'
import { SearchIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import * as ROUTES from '../../constants/routes'
import { Section } from './Section'
import { Details } from './Details'
import { FilterDropdown, SectionFilterState } from './FilterDropdown'
import {
  CommandPaletteOption,
  CompanyEntry,
  CompanyGroupEntry,
  getValue,
  HighlightedText,
  PaymentEntry,
  PositionEntry,
  RouteEntry,
  ShiftEntry,
  UserEntry,
  WorkItemEntry,
} from './CommandPalette_Utils'
import { useCompaniesQuery } from '../../queries/companies/useCompaniesQuery'
import { useCompanyGroupsQuery } from '../../queries/companyGroups/useCompanyGroupsQuery'
import { useFuzzySearchUsersQuery } from '../../queries/users/useFuzzySearchUsersQuery'
import { useFuzzySearchPositionsQuery } from '../../queries/positions/useFuzzySearchPositionsQuery'
import { useFastIdSearchQuery } from '../../queries/fastIdSearch/useFastIdSearchQuery'
import {
  addToHistory,
  clearHistory,
  getHistoryByType,
  HistoryType,
} from './history'

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100000;
`

export function CommandPalette() {
  const navigate = useNavigate()

  // ==================== STATE ====================
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [selectedItem, setSelectedItem] = useState<CommandPaletteOption | null>(
    null
  )
  const [enabledSections, setEnabledSections] = useState<SectionFilterState>({
    companies: true,
    company_groups: true,
    users: true,
    shifts: true,
    work_items: true,
    payments: true,
    positions: true,
    routes: true,
  })
  const [, setHistoryRefresh] = useState(0)

  // ==================== REFS ====================
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const itemRefsRef = useRef<React.RefObject<HTMLDivElement | null>[]>([])

  // ==================== DATA QUERIES ====================
  const { data: companiesData } = useCompaniesQuery()
  const { data: companyGroupsData } = useCompanyGroupsQuery()
  const { data: users, isLoading: isLoadingUsers } = useFuzzySearchUsersQuery({
    search: debouncedSearchValue,
    limit: 6,
    enabled: enabledSections.users && debouncedSearchValue.trim().length > 0,
  })
  const { data: positions = [], isLoading: isLoadingPositions } =
    useFuzzySearchPositionsQuery({
      search: debouncedSearchValue,
      limit: 3,
      enabled:
        enabledSections.positions && debouncedSearchValue.trim().length > 0,
    })
  const shouldFetchFastIdSearch =
    debouncedSearchValue.trim().length > 0 &&
    /^\d+$/.test(debouncedSearchValue.trim()) &&
    (enabledSections.shifts ||
      enabledSections.work_items ||
      enabledSections.payments)
  const { data: fastIdSearchData, isLoading: isLoadingFastIdSearch } =
    useFastIdSearchQuery({
      search: debouncedSearchValue.trim(),
      limit: 3,
      enabled: shouldFetchFastIdSearch,
    })

  // ==================== CONFIGURATION ====================
  const fuzzyOptions: FullOptions<CommandPaletteOption> = {
    threshold: 0.7,
    ignoreCase: true,
    ignoreSymbols: true,
    normalizeWhitespace: true,
    returnMatchData: true,
    keySelector: (obj) => [
      String('name' in obj ? obj.name : ''),
      String('id' in obj ? obj.id : ''),
    ],
  }

  // ==================== HELPER FUNCTIONS ====================
  const createFuzzySearcher = <T extends CommandPaletteOption>(
    data: T[] | undefined
  ) => {
    if (!data) return null
    return new Searcher<T, FullOptions<CommandPaletteOption>>(
      data,
      fuzzyOptions
    )
  }

  const filterWithSearcher = <T extends CommandPaletteOption>(
    searcher: Searcher<T, FullOptions<CommandPaletteOption>> | null,
    query: string,
    limit = 4
  ): T[] => {
    if (!query.trim() || !searcher) return []
    return (searcher.search(query, fuzzyOptions) as unknown as MatchData<T>[])
      .map((i) => i.item)
      .slice(0, limit)
  }

  // ==================== COMPUTED VALUES ====================
  const trimmed = debouncedSearchValue.trim()
  const isNumericId = trimmed.length > 0 && /^\d+$/.test(trimmed)

  // User results sorting
  let sortedUsers: UserEntry[] = users
    ? users.map((user) => ({ ...user, historyType: 'users' }))
    : []
  if (users && debouncedSearchValue.trim()) {
    const normalizedQuery = debouncedSearchValue.trim().toLowerCase()
    const getPriority = (user: UserEntry): number => {
      if (String(user.id).toLowerCase().startsWith(normalizedQuery)) return 1
      if (getValue(user.email).toLowerCase().startsWith(normalizedQuery))
        return 2
      return 3
    }
    sortedUsers = [...sortedUsers].sort((a, b) => {
      const priorityA = getPriority(a)
      const priorityB = getPriority(b)
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }
      return 0
    })
  }

  // Fast ID search data
  const shiftAutocompleteItems = fastIdSearchData?.shifts
  const paymentAutocompleteItems = fastIdSearchData?.payments
  const workItemAutocompleteItems = fastIdSearchData?.workItems
  const isLoadingShift = isLoadingFastIdSearch
  const isLoadingWorkItem = isLoadingFastIdSearch
  const isLoadingPayment = isLoadingFastIdSearch

  // Fuzzy search setup
  const companiesWithHistoryType: CompanyEntry[] = companiesData
    ? companiesData.map((company) => ({
        ...company,
        type: company.type === 'W2' ? 'w2' : '1099',
        historyType: 'companies',
      }))
    : []
  const companySearcher = createFuzzySearcher(companiesWithHistoryType)
  const companyGroupsWithType: CompanyGroupEntry[] = companyGroupsData
    ? companyGroupsData.map((group) => ({
        ...group,
        historyType: 'company_groups',
      }))
    : []
  const companyGroupSearcher = createFuzzySearcher(companyGroupsWithType)

  // Fuzzy search results
  const filteredCompanies: CompanyEntry[] = filterWithSearcher(
    companySearcher,
    debouncedSearchValue
  )
  const filteredCompanyGroups: CompanyGroupEntry[] = filterWithSearcher(
    companyGroupSearcher,
    debouncedSearchValue
  )
  const filteredRoutes: RouteEntry[] = !debouncedSearchValue.trim()
    ? []
    : Object.entries(ROUTES)
        .filter(
          (route) =>
            String(route[0])
              .toLowerCase()
              .includes(debouncedSearchValue.toLowerCase()) &&
            !route[1].includes(':') // filter out routes like /users/:id
        )
        .map(([key, path]) => ({
          id: key,
          path: path as string,
          label: key,
          historyType: 'routes',
        }))

  // History data
  const {
    users: historyUsers,
    companies: historyCompanies,
    company_groups: historyCompanyGroups,
    routes: historyRoutes,
    shifts: historyShifts,
    work_items: historyWorkItems,
    payments: historyPayments,
    positions: historyPositions,
  } = getHistoryByType(searchValue.trim().length > 0)

  // Prepare shifts, work items and payments for display
  const shiftItems: ShiftEntry[] =
    shiftAutocompleteItems && isNumericId ? shiftAutocompleteItems : []
  const workItemItems: WorkItemEntry[] =
    workItemAutocompleteItems && isNumericId ? workItemAutocompleteItems : []
  const paymentItems: PaymentEntry[] =
    paymentAutocompleteItems && isNumericId ? paymentAutocompleteItems : []

  // Create flattened list of all items for navigation
  const hasSearch = searchValue.trim().length > 0
  const allItems: CommandPaletteOption[] = []

  const addItemsIfVisible = (
    filteredItems: CommandPaletteOption[],
    historyItems: CommandPaletteOption[],
    isLoading?: boolean
  ) => {
    const shouldShow = !hasSearch || filteredItems.length > 0 || isLoading
    if (shouldShow) {
      const itemsToAdd = hasSearch ? filteredItems : historyItems
      allItems.push(...itemsToAdd)
    }
  }

  if (enabledSections.companies) {
    addItemsIfVisible(filteredCompanies, historyCompanies)
  }
  if (enabledSections.company_groups) {
    addItemsIfVisible(filteredCompanyGroups, historyCompanyGroups)
  }
  if (enabledSections.users) {
    addItemsIfVisible(sortedUsers || [], historyUsers, isLoadingUsers)
  }
  if (enabledSections.shifts) {
    addItemsIfVisible(
      isNumericId ? shiftItems : [],
      isNumericId ? [] : historyShifts,
      isLoadingShift
    )
  }
  if (enabledSections.work_items) {
    addItemsIfVisible(
      isNumericId ? workItemItems : [],
      isNumericId ? [] : historyWorkItems,
      isLoadingWorkItem
    )
  }
  if (enabledSections.payments) {
    addItemsIfVisible(
      isNumericId ? paymentItems : [],
      isNumericId ? [] : historyPayments,
      isLoadingPayment
    )
  }
  if (enabledSections.positions) {
    addItemsIfVisible(positions, historyPositions, isLoadingPositions)
  }
  if (enabledSections.routes) {
    addItemsIfVisible(filteredRoutes, historyRoutes)
  }

  // Item refs setup
  if (itemRefsRef.current.length !== allItems.length) {
    itemRefsRef.current = allItems.map(() => createRef<HTMLDivElement | null>())
  }
  const itemRefs = itemRefsRef.current

  const getItemRef = (item: CommandPaletteOption) => {
    const index = allItems.findIndex((allItem) => _.isEqual(allItem, item))
    return index >= 0 ? itemRefs[index] : null
  }

  // ==================== EVENT HANDLERS ====================
  const handleClearHistory = (type: HistoryType) => {
    clearHistory(type)
    setHistoryRefresh((prev) => prev + 1)
  }

  const handleItemHover = (item: CommandPaletteOption) => {
    setSelectedItem(item)
  }

  const handleArrowNavigation = (direction: 'up' | 'down') => {
    if (allItems.length === 0) return

    let currentIndex = -1
    if (selectedItem) {
      currentIndex = allItems.findIndex((item) => _.isEqual(item, selectedItem))
    }

    if (currentIndex === -1) {
      currentIndex = direction === 'down' ? 0 : allItems.length - 1
    } else {
      if (direction === 'down') {
        currentIndex = (currentIndex + 1) % allItems.length
      } else {
        currentIndex =
          currentIndex === 0 ? allItems.length - 1 : currentIndex - 1
      }
    }

    setSelectedItem(allItems[currentIndex])
  }

  const handleItemInteraction = (item: CommandPaletteOption) => {
    addToHistory(item)
  }

  const closePalette = () => {
    setIsOpen(false)
    setSearchValue('')
  }

  const handleSectionToggle = (
    section: keyof SectionFilterState,
    enabled: boolean
  ) => {
    setEnabledSections((prev) => ({
      ...prev,
      [section]: enabled,
    }))
  }

  const handleContentClick = (e: React.MouseEvent) => e.stopPropagation()

  // ==================== EFFECTS ====================
  // Debounce search value and clear selected item
  useEffect(() => {
    setSelectedItem(null)
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchValue])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // cmd+K or ctrl+K
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        setIsOpen((prev) => !prev)
      }

      if (event.key === 'Escape' && isOpen) {
        closePalette()
      }

      // Arrow key navigation
      if (
        isOpen &&
        (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
        allItems.length > 0
      ) {
        event.preventDefault()
        handleArrowNavigation(event.key === 'ArrowDown' ? 'down' : 'up')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, allItems, handleArrowNavigation, inputRef])

  // Auto-focus input when palette opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Scroll selected item into view during arrow key navigation
  useEffect(() => {
    if (!selectedItem) return

    const index = allItems.findIndex((item) => _.isEqual(item, selectedItem))
    if (index >= 0 && itemRefs[index]?.current) {
      itemRefs[index].current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [selectedItem, allItems, itemRefs])

  // ==================== RENDER ====================
  return (
    isOpen && (
      <Overlay onClick={closePalette}>
        <Box // search input
          onClick={handleContentClick}
          style={{
            width: '800px',
            backgroundColor: 'white',
            padding: '16px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            borderRadius: '8px 8px 0px 0px',
          }}
        >
          <div style={{ flex: 1 }}>
            <Input
              ref={inputRef}
              name="search"
              type="text"
              icon={<Icon color="lightText" size="20" icon={SearchIcon} />}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
              placeholder="Type to search..."
              block
              autoComplete="off"
            />
          </div>
          <FilterDropdown
            enabledSections={enabledSections}
            onSectionToggle={handleSectionToggle}
          />
        </Box>
        <Box // results
          onClick={handleContentClick}
          style={{
            width: '800px',
            height: '80vh',
            backgroundColor: '#f8f9fa',
            padding: 0,
            display: 'flex',
            flexDirection: 'row',
            borderRadius: '0px 0px 8px 8px',
          }}
        >
          <div // left side
            ref={scrollContainerRef}
            style={{
              width: '50%',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #e5e7eb',
              borderRadius: '0px 0px 0px 8px',
              backgroundColor: 'white',
              overflowY: 'auto',
            }}
          >
            <Section<CompanyEntry>
              title="COMPANIES"
              items={searchValue.trim() ? filteredCompanies : historyCompanies}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('companies')}
              getItemRef={getItemRef}
              renderItem={(company, searchQuery) => {
                const companyName = getValue(company.name)
                const companyId = String(company.id || '')
                const normalizedQuery = searchQuery.trim().toLowerCase()
                const idMatches =
                  normalizedQuery &&
                  companyId.toLowerCase().includes(normalizedQuery)

                const description = (
                  <>
                    ID:{' '}
                    {searchQuery.trim() && idMatches ? (
                      <HighlightedText text={companyId} query={searchQuery} />
                    ) : (
                      companyId
                    )}{' '}
                    | Type: {getValue(company.type)}
                  </>
                )

                return {
                  title: searchQuery.trim() ? (
                    <HighlightedText text={companyName} query={searchQuery} />
                  ) : (
                    companyName
                  ),
                  description: description,
                }
              }}
              emptyStateMessage="No companies found"
              hide={
                !enabledSections.companies ||
                (!!searchValue.trim() && filteredCompanies?.length === 0)
              }
              onItemClick={(company) => {
                addToHistory(company)
                navigate(`/companies/${company.id}`)
                closePalette()
              }}
            />
            <Section<CompanyGroupEntry>
              title="COMPANY GROUPS"
              items={
                searchValue.trim()
                  ? filteredCompanyGroups
                  : historyCompanyGroups
              }
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('company_groups')}
              getItemRef={getItemRef}
              renderItem={(companyGroup, searchQuery) => {
                const groupName = getValue(companyGroup.name)
                const description = `ID: ${companyGroup.id} | Companies: ${getValue(companyGroup.companyCount)}`
                return {
                  title: searchQuery.trim() ? (
                    <HighlightedText text={groupName} query={searchQuery} />
                  ) : (
                    groupName
                  ),
                  description: description,
                }
              }}
              emptyStateMessage="No company groups found"
              hide={
                !enabledSections.company_groups ||
                (!!searchValue.trim() && filteredCompanyGroups?.length === 0)
              }
              onItemClick={(companyGroup) => {
                addToHistory(companyGroup)
                navigate(`/company-groups/${companyGroup.id}`)
                closePalette()
              }}
            />
            <Section<UserEntry>
              title="USERS"
              items={searchValue.trim() ? sortedUsers || [] : historyUsers}
              isLoading={isLoadingUsers}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('users')}
              getItemRef={getItemRef}
              renderItem={(user, searchQuery) => {
                const userName = getValue(user.name)
                const userEmail = getValue(user.email)
                const normalizedQuery = searchQuery.trim().toLowerCase()
                const nameMatches =
                  normalizedQuery &&
                  userName.toLowerCase().startsWith(normalizedQuery)
                const emailMatches =
                  normalizedQuery &&
                  userEmail.toLowerCase().startsWith(normalizedQuery)

                return {
                  title:
                    searchQuery.trim() && nameMatches && !emailMatches ? (
                      <HighlightedText text={userName} query={searchQuery} />
                    ) : (
                      userName
                    ),
                  description:
                    searchQuery.trim() && emailMatches ? (
                      <HighlightedText text={userEmail} query={searchQuery} />
                    ) : (
                      userEmail
                    ),
                }
              }}
              emptyStateMessage="No users found"
              hide={
                !enabledSections.users ||
                (!!searchValue.trim() &&
                  sortedUsers?.length === 0 &&
                  !isLoadingUsers)
              }
              onItemClick={(user) => {
                addToHistory(user)
                navigate(`/users/${user.id}`)
                closePalette()
              }}
            />
            <Section<ShiftEntry>
              title="SHIFTS"
              items={isNumericId ? shiftItems : historyShifts}
              isLoading={isLoadingShift}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('shifts')}
              getItemRef={getItemRef}
              renderItem={(shift) => ({
                title: `Shift #${getValue(shift.id)}`,
                description: shift.startsAt
                  ? `Starts: ${new Date(String(shift.startsAt)).toLocaleString()}`
                  : 'N/A',
              })}
              emptyStateMessage="No shift found. Enter a shift ID to search."
              hide={
                !enabledSections.shifts ||
                (!!searchValue.trim() &&
                  shiftItems?.length === 0 &&
                  !isLoadingShift)
              }
              onItemClick={(shift) => {
                addToHistory(shift)
                navigate(
                  ROUTES.SHIFT_DETAIL.replace(':shiftId', String(shift.id))
                )
                closePalette()
              }}
            />
            <Section<WorkItemEntry>
              title="WORK ITEMS"
              items={isNumericId ? workItemItems : historyWorkItems}
              isLoading={isLoadingWorkItem}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('work_items')}
              getItemRef={getItemRef}
              renderItem={(workItem) => ({
                title: `Work Item #${getValue(workItem.id)}`,
                description: `User: ${getValue(workItem.workerId)} | Shift: ${getValue(workItem.shiftId)} | Status: ${getValue(workItem.status)}`,
              })}
              emptyStateMessage="No work item found. Enter a work ID to search."
              hide={
                !enabledSections.work_items ||
                (!!searchValue.trim() &&
                  workItemItems?.length === 0 &&
                  !isLoadingWorkItem)
              }
              onItemClick={(workItem) => {
                addToHistory(workItem)
                navigate(
                  ROUTES.SHIFT_DETAIL.replace(
                    ':shiftId',
                    String(workItem.shiftId)
                  )
                )
                closePalette()
              }}
            />
            <Section<PaymentEntry>
              title="PAYMENTS"
              items={isNumericId ? paymentItems : historyPayments}
              isLoading={isLoadingPayment}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('payments')}
              getItemRef={getItemRef}
              renderItem={(payment) => ({
                title: `Payment #${getValue(payment.id)}`,
                description: `Amount: $${getValue(payment.amount)} | Status: ${getValue(payment.status)}`,
              })}
              emptyStateMessage="No payment found. Enter a payment ID to search."
              hide={
                !enabledSections.payments ||
                (!!searchValue.trim() &&
                  paymentItems?.length === 0 &&
                  !isLoadingPayment)
              }
              onItemClick={(payment) => {
                addToHistory(payment)
                navigate(
                  `${ROUTES.USER_PAYMENTS}?status=&event-type=&event-sub-type=&type=&payment-provider=&payment-id=${payment.id}`
                )
                closePalette()
              }}
            />
            <Section<PositionEntry>
              title="POSITIONS"
              items={searchValue.trim() ? positions || [] : historyPositions}
              isLoading={isLoadingPositions}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('positions')}
              getItemRef={getItemRef}
              renderItem={(position, searchQuery) => {
                const positionName = getValue(position.name)
                return {
                  title: searchQuery.trim() ? (
                    <HighlightedText text={positionName} query={searchQuery} />
                  ) : (
                    positionName
                  ),
                  description: position.companyName,
                }
              }}
              emptyStateMessage="No positions found"
              hide={
                !enabledSections.positions ||
                (!!searchValue.trim() &&
                  positions?.length === 0 &&
                  !isLoadingPositions)
              }
              onItemClick={(position) => {
                addToHistory(position)
                navigate(`${ROUTES.POSITION_EDITOR}?positionId=${position.id}`)
                closePalette()
              }}
            />
            <Section<RouteEntry>
              title="ROUTES"
              items={searchValue.trim() ? filteredRoutes : historyRoutes}
              searchValue={searchValue}
              selectedItem={selectedItem}
              onItemClick={(route) => {
                addToHistory(route)
                navigate(String(route.path))
                closePalette()
              }}
              onItemHover={handleItemHover}
              onClearHistory={() => handleClearHistory('routes')}
              getItemRef={getItemRef}
              renderItem={(route) => ({
                title: getValue(route.label),
                description: getValue(route.path),
              })}
              emptyStateMessage="No routes found"
              hideBottomBorder
              hide={
                !enabledSections.routes ||
                (!!searchValue.trim() && filteredRoutes?.length === 0)
              }
            />
          </div>
          <Details
            selectedItem={selectedItem}
            onNavigateToUser={(userId) => {
              navigate(`/users/${userId}`)
              closePalette()
            }}
            onNavigateToCompany={(companyId) => {
              navigate(`/companies/${companyId}`)
              closePalette()
            }}
            onNavigateToCompanyGroup={(companyGroupId) => {
              navigate(
                ROUTES.COMPANY_GROUP_DETAIL.replace(
                  ':id',
                  String(companyGroupId)
                )
              )
              closePalette()
            }}
            onNavigateToRoute={() => {
              if (selectedItem && selectedItem.historyType === 'routes') {
                navigate(selectedItem.path)
              }
              closePalette()
            }}
            onNavigateToShift={(shiftId) => {
              navigate(ROUTES.SHIFT_DETAIL.replace(':shiftId', String(shiftId)))
              closePalette()
            }}
            onNavigateToPayment={(paymentId) => {
              navigate(
                `${ROUTES.USER_PAYMENTS}?status=&event-type=&event-sub-type=&type=&payment-provider=&payment-id=${paymentId}`
              )
              closePalette()
            }}
            onNavigateToPosition={(positionId) => {
              navigate(`${ROUTES.POSITION_EDITOR}?positionId=${positionId}`)
              closePalette()
            }}
            onItemInteraction={handleItemInteraction}
          />
        </Box>
      </Overlay>
    )
  )
}
